import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeSwitcher } from "@/components/ui/theme-switcher";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Suspense, lazy } from "react";
import Home from "./pages/Home";
import ErrorPage from "./pages/ErrorPage";

// Lazy load pages for better performance
const SearchResults = lazy(() => import("./pages/SearchResults"));
const Cart = lazy(() => import("./pages/Cart"));
const Confirmation = lazy(() => import("./pages/Confirmation"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const Pricing = lazy(() => import("./pages/Pricing"));

// Hosting pages
const SharedHosting = lazy(() => import("./pages/SharedHosting"));
const WordPressHosting = lazy(() => import("./pages/WordPressHosting"));
const ResellerHosting = lazy(() => import("./pages/ResellerHosting"));
const VPSHosting = lazy(() => import("./pages/VPSHosting"));
const DedicatedServers = lazy(() => import("./pages/DedicatedServers"));
const MigrateToUS = lazy(() => import("./pages/MigrateToUS"));

// Security pages
const Security = lazy(() => import("./pages/Security"));
const SSLCertificates = lazy(() => import("./pages/SSLCertificates"));
const DomainPrivacy = lazy(() => import("./pages/DomainPrivacy"));
const FastVPN = lazy(() => import("./pages/FastVPN"));
const PremiumDNS = lazy(() => import("./pages/PremiumDNS"));
const TwoFactorAuth = lazy(() => import("./pages/TwoFactorAuth"));
const WebsiteSecurity = lazy(() => import("./pages/WebsiteSecurity"));

// Marketing Tools pages
const BusinessStarterKit = lazy(() => import("./pages/marketing/BusinessStarterKit"));
const LogoMaker = lazy(() => import("./pages/marketing/LogoMaker"));
const BusinessNameGenerator = lazy(() => import("./pages/marketing/BusinessNameGenerator"));
const SiteMaker = lazy(() => import("./pages/marketing/SiteMaker"));
const StencilGraphics = lazy(() => import("./pages/marketing/StencilGraphics"));
const BusinessCardMaker = lazy(() => import("./pages/marketing/BusinessCardMaker"));
const RelateSocialAI = lazy(() => import("./pages/marketing/RelateSocialAI"));
const RelateSEO = lazy(() => import("./pages/marketing/RelateSEO"));
const RelateReviews = lazy(() => import("./pages/marketing/RelateReviews"));

// Domain pages
const DomainSearch = lazy(() => import("./pages/DomainSearch"));
const DomainTransfer = lazy(() => import("./pages/DomainTransfer"));
const DomainPricing = lazy(() => import("./pages/DomainPricing"));
const WhoisLookup = lazy(() => import("./pages/WhoisLookup"));

// Help Center pages
const HelpCenter = lazy(() => import("./pages/help/HelpCenter.tsx"));
const Knowledgebase = lazy(() => import("./pages/help/Knowledgebase.tsx"));
const GuruGuides = lazy(() => import("./pages/help/GuruGuides.tsx"));
const Blog = lazy(() => import("./pages/help/Blog.tsx"));
const HowToVideos = lazy(() => import("./pages/help/HowToVideos.tsx"));
const StatusUpdates = lazy(() => import("./pages/help/StatusUpdates.tsx"));
const ContactUs = lazy(() => import("./pages/help/ContactUs.tsx"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <ThemeSwitcher />
      <BrowserRouter>
        <Suspense fallback={
          <div className="min-h-screen w-full flex items-center justify-center">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
          </div>
        }>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/search-results" element={<SearchResults />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/confirmation" element={<Confirmation />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about" element={<About />} />
            <Route path="/pricing" element={<Pricing />} />
            
            {/* Hosting pages */}
            <Route path="/hosting/shared" element={<SharedHosting />} />
            <Route path="/hosting/wordpress" element={<WordPressHosting />} />
            <Route path="/hosting/reseller" element={<ResellerHosting />} />
            <Route path="/hosting/vps" element={<VPSHosting />} />
            <Route path="/hosting/dedicated" element={<DedicatedServers />} />
            <Route path="/hosting/migrate" element={<MigrateToUS />} />
            
            {/* Security pages */}
            <Route path="/security" element={<Security />} />
            <Route path="/security/ssl-certificates" element={<SSLCertificates />} />
            <Route path="/security/domain-privacy" element={<DomainPrivacy />} />
            <Route path="/security/fastvpn" element={<FastVPN />} />
            <Route path="/security/premium-dns" element={<PremiumDNS />} />
            <Route path="/security/two-factor-auth" element={<TwoFactorAuth />} />
            <Route path="/security/website-security" element={<WebsiteSecurity />} />
            
            {/* Marketing Tools pages */}
            <Route path="/marketing/business-starter-kit" element={<BusinessStarterKit />} />
            <Route path="/marketing/logo-maker" element={<LogoMaker />} />
            <Route path="/marketing/business-name-generator" element={<BusinessNameGenerator />} />
            <Route path="/marketing/site-maker" element={<SiteMaker />} />
            <Route path="/marketing/stencil-graphics" element={<StencilGraphics />} />
            <Route path="/marketing/business-card-maker" element={<BusinessCardMaker />} />
            <Route path="/marketing/relate-social-ai" element={<RelateSocialAI />} />
            <Route path="/marketing/relate-seo" element={<RelateSEO />} />
            <Route path="/marketing/relate-reviews" element={<RelateReviews />} />
            
            {/* Domain pages */}
            <Route path="/domains" element={<DomainSearch />} />
            <Route path="/domains/search" element={<DomainSearch />} />
            <Route path="/domains/transfer" element={<DomainTransfer />} />
            <Route path="/domains/pricing" element={<DomainPricing />} />
            <Route path="/domains/whois" element={<WhoisLookup />} />
            
            {/* Help Center pages */}
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/help/knowledgebase" element={<Knowledgebase />} />
            <Route path="/help/guru-guides" element={<GuruGuides />} />
            <Route path="/help/blog" element={<Blog />} />
            <Route path="/help/videos" element={<HowToVideos />} />
            <Route path="/help/status" element={<StatusUpdates />} />
            <Route path="/help/contact" element={<ContactUs />} />
            
            <Route path="*" element={<ErrorPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;